import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useContactsToTriageTotal, useTagDefs, type TagDef, type TagKind } from "../data";
import {
  backfillFamilies,
  createTagDef,
  dedupeTimeline,
  deleteTagDef,
  downloadFile,
  exportPeople,
  mergeTagDefs,
  seedTaxonomy,
  tagUsageCounts,
  updateTagDef,
} from "../actions";
import { useAuth } from "../auth";
import {
  IcAlert,
  IcChevron,
  IcDownload,
  IcMerge,
  IcPeople,
  IcPlus,
  IcSearch,
  IcSeed,
  IcWhatsapp,
} from "../ui/icons";
import { GoogleCalendarCard } from "../ui/GoogleCalendarCard";
import { GoogleContactsCard } from "../ui/GoogleContactsCard";
import { KodeshboardCard } from "../ui/KodeshboardCard";

type SettingsTab = "connections" | "tags" | "data";

const LISTS: { kind: TagKind; shortLabel: string; title: string; hint: string }[] = [
  {
    kind: "person",
    shortLabel: "אנשי קשר",
    title: "תגיות לאנשי קשר",
    hint: "מה הקשר של כל אחד לבית חב״ד. תגית נשארת עד שמישהו משנה אותה.",
  },
  {
    kind: "business_service",
    shortLabel: "עסקים ושירותים",
    title: "תחומי עסקים ושירותים",
    hint: "מה עסק בשכונה מציע: שיפוצים, צילום, טיפוח וכו׳.",
  },
  {
    kind: "organization_type",
    shortLabel: "מוסדות",
    title: "סוגי מוסדות",
    hint: "בית כנסת, גן, בית ספר ומוסדות בקהילה.",
  },
];

export function SettingsPage() {
  const { user, role } = useAuth();
  const isAdmin = role === "owner" || role === "admin";
  const [tab, setTab] = useState<SettingsTab>("connections");
  const [activeTagKind, setActiveTagKind] = useState<TagKind>("person");

  const [counts, setCounts] = useState<Record<string, number> | null>(null);
  const [countsError, setCountsError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    tagUsageCounts()
      .then((c) => !cancelled && setCounts(c))
      .catch(() => !cancelled && setCountsError(true));
    return () => {
      cancelled = true;
    };
  }, [isAdmin, reloadKey]);

  const activeList = LISTS.find((l) => l.kind === activeTagKind) ?? LISTS[0]!;

  return (
    <div className="screen">
      <header className="hdr">
        <div className="hdr-top">
          <div className="hdr-titles">
            <h1>הגדרות ונתונים</h1>
            <p className="sub">חיבורים חיצוניים, ניהול תגיות וכלי תחזוקת נתונים</p>
          </div>
        </div>

        <div className="pilltabs" role="tablist">
          <button
            type="button"
            className={tab === "connections" ? "on" : ""}
            onClick={() => setTab("connections")}
          >
            חיבורים והודעות
          </button>
          <button
            type="button"
            className={tab === "tags" ? "on" : ""}
            onClick={() => setTab("tags")}
          >
            תגיות וקטגוריות
          </button>
          <button
            type="button"
            className={tab === "data" ? "on" : ""}
            onClick={() => setTab("data")}
          >
            ניקיון ונתונים
          </button>
        </div>
      </header>

      <div className="body">
        {tab === "connections" && (
          <>
            <section className="section">
              <div className="section-head">
                <div className="section-title">חיבורים חיצוניים</div>
              </div>
              <GoogleContactsCard />
              <GoogleCalendarCard />
              <KodeshboardCard />
            </section>

            <section className="section">
              <div className="section-head">
                <div className="section-title">תבניות הודעות</div>
              </div>
              <div className="card">
                <Link to="/messages" className="list-row">
                  <span className="rail-ico tint-green">
                    <IcWhatsapp />
                  </span>
                  <span className="grow">
                    נוסחי הודעות וואטסאפ
                    <span className="faint" style={{ display: "block", marginTop: 2 }}>
                      עריכת נוסח ההודעות שנפתחות בלחיצה על וואטסאפ (תודה על תרומה, יום הולדת, יארצייט ועוד)
                    </span>
                  </span>
                  <IcChevron size={17} />
                </Link>
              </div>
            </section>
          </>
        )}

        {tab === "tags" && (
          <section className="section">
            <div className="chips" style={{ marginBottom: 14 }}>
              {LISTS.map((l) => (
                <button
                  key={l.kind}
                  type="button"
                  className={activeTagKind === l.kind ? "tagchip tagchip-on" : "tagchip"}
                  onClick={() => setActiveTagKind(l.kind)}
                >
                  {l.shortLabel}
                </button>
              ))}
            </div>

            {countsError && (
              <div className="notice error" style={{ marginBottom: 14 }}>
                לא הצלחנו לספור כמה אנשי קשר יש בכל תגית. אפשר עדיין לערוך או למחוק, ואת המספר המדויק תראו בשלב האישור.
              </div>
            )}

            <TagList
              key={activeList.kind}
              {...activeList}
              counts={counts}
              isAdmin={isAdmin}
              onChanged={() => setReloadKey((k) => k + 1)}
            />
          </section>
        )}

        {tab === "data" && <DataSection isAdmin={isAdmin} />}

        <p className="faint" style={{ padding: "4px 6px 8px" }}>
          מחוברים כ־{user?.email}
        </p>
      </div>
    </div>
  );
}

function DataSection({ isAdmin }: { isAdmin: boolean }) {
  const triageTotal = useContactsToTriageTotal();
  const [busy, setBusy] = useState<"export" | "backfill" | "dedupe" | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function exportToExcel() {
    setBusy("export");
    setMsg(null);
    setErr(null);
    try {
      const r = await exportPeople();
      const day = new Date().toLocaleDateString("en-CA");
      downloadFile(`chbapark-anashim-${day}.csv`, r.csv, "text/csv");
      setMsg(
        `הקובץ ירד בהצלחה: ${r.people.toLocaleString("he-IL")} אנשי קשר (${r.withPhone.toLocaleString("he-IL")} עם טלפון, ${r.withEmail.toLocaleString("he-IL")} עם אימייל).`,
      );
    } catch (e) {
      const m = (e as { message?: string })?.message;
      setErr(m && /[֐-׿]/.test(m) ? m : "לא הצלחנו לייצא לאקסל. נסו שוב.");
    } finally {
      setBusy(null);
    }
  }

  async function runBackfill() {
    setBusy("backfill");
    setMsg(null);
    setErr(null);
    try {
      const r = await backfillFamilies();
      setMsg(
        `מצאנו ${r.realFamilies.toLocaleString("he-IL")} משפחות אמיתיות, ` +
          `ו־${r.addressGroups.toLocaleString("he-IL")} קבוצות כתובת. ` +
          `${r.coupleRecords} כרטיסי זוגות, ו־${r.peopleSharingAPhone.toLocaleString("he-IL")} אנשי קשר שחולקים טלפון.`,
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו לחשב משפחות. נסו שוב.");
    } finally {
      setBusy(null);
    }
  }

  async function runDedupe() {
    setBusy("dedupe");
    setMsg(null);
    setErr(null);
    try {
      const preview = await dedupeTimeline(true);
      if (preview.duplicateGroups === 0) {
        setMsg("אין כפילויות בציר הזמן — הכול נקי.");
        return;
      }
      const ok = confirm(
        `מצאנו ${preview.duplicateGroups} אירועים שנרשמו יותר מפעם אחת אצל ${preview.peopleAffected} אנשי קשר.\n` +
          `${preview.entriesToRemove} שורות כפולות יאוחדו לשורה אחת.\n\n` +
          `הנתונים המקוריים לא נמחקים — רק התצוגה מתאחדת. להמשיך?`,
      );
      if (!ok) return;

      const r = await dedupeTimeline();
      setMsg(
        `איחדנו ${r.entriesRemoved.toLocaleString("he-IL")} שורות כפולות ` +
          `ב־${r.groupsMerged.toLocaleString("he-IL")} אירועים.` +
          (r.failedGroups > 0 ? ` ב־${r.failedGroups} אירועים אירעה שגיאה.` : ""),
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו לאחד כפילויות. נסו שוב.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <section className="section">
        <div className="section-head">
          <div className="section-title">מיון ואיחוד כרטיסים</div>
        </div>
        <div className="card">
          <Link to="/triage" className="list-row">
            <span className="rail-ico tint-orange">
              <IcPeople />
            </span>
            <span className="grow">
              מיון אנשי קשר מגוגל {triageTotal !== null && triageTotal > 0 ? `(${triageTotal})` : ""}
              <span className="faint" style={{ display: "block", marginTop: 2 }}>
                הוספה בלחיצה אחת ל-CRM או ניקוי מהיר של כל הרשימה הישנה מגוגל
              </span>
            </span>
            <IcChevron size={17} />
          </Link>

          <Link to="/duplicates" className="list-row">
            <span className="rail-ico tint-slate">
              <IcMerge />
            </span>
            <span className="grow">
              כפילויות ומיזוג כרטיסים
              <span className="faint" style={{ display: "block", marginTop: 2 }}>
                זיהוי ואיחוד אנשי קשר שמופיעים פעמיים עם אותו טלפון או אימייל
              </span>
            </span>
            <IcChevron size={17} />
          </Link>
        </div>
      </section>

      {isAdmin && (
        <section className="section">
          <div className="section-head">
            <div className="section-title">ייצוא, ייבוא ותחזוקת מאגר</div>
          </div>

          <div className="card">
            <button
              className="list-row"
              onClick={exportToExcel}
              disabled={busy !== null}
              style={{ width: "100%", textAlign: "start" }}
            >
              <span className="rail-ico tint-green">
                <IcDownload />
              </span>
              <span className="grow">
                {busy === "export" ? "מכין קובץ אקסל…" : "ייצוא כל אנשי הקשר לאקסל (CSV)"}
                <span className="faint" style={{ display: "block", marginTop: 2 }}>
                  הורדת קובץ מסודר עם כל השמות, הטלפונים והמיילים במערכת
                </span>
              </span>
              <IcChevron size={17} />
            </button>

            <button
              className="list-row"
              onClick={runBackfill}
              disabled={busy !== null}
              style={{ width: "100%", textAlign: "start" }}
            >
              <span className="rail-ico tint-green">
                <IcPeople />
              </span>
              <span className="grow">
                {busy === "backfill" ? "מחשב משפחות…" : "חישוב משפחות מחדש"}
                <span className="faint" style={{ display: "block", marginTop: 2 }}>
                  משייך בני בית למשפחות ומזהה זוגות וטלפונים משותפים
                </span>
              </span>
            </button>

            <button
              className="list-row"
              onClick={runDedupe}
              disabled={busy !== null}
              style={{ width: "100%", textAlign: "start" }}
            >
              <span className="rail-ico tint-slate">
                <IcSeed />
              </span>
              <span className="grow">
                {busy === "dedupe" ? "מאחד כפילויות…" : "איחוד שורות כפולות בציר הזמן"}
                <span className="faint" style={{ display: "block", marginTop: 2 }}>
                  ניקוי אירועים שנרשמו פעמיים בייבוא ההיסטורי בלי למחוק מידע מקורי
                </span>
              </span>
            </button>

            <Link to="/import" className="list-row">
              <span className="rail-ico tint-amber">
                <IcSeed />
              </span>
              <span className="grow">
                ייבוא היסטורי מקובץ
                <span className="faint" style={{ display: "block", marginTop: 2 }}>
                  טעינת אנשי קשר, משפחות, אירועים ותרומות מקובץ חיצוני
                </span>
              </span>
              <IcChevron size={17} />
            </Link>
          </div>

          {msg && <div className="notice info">{msg}</div>}
          {err && <div className="notice error">{err}</div>}
        </section>
      )}
    </>
  );
}

type Pending =
  | { mode: "delete"; tag: TagDef; people: number }
  | { mode: "merge"; tag: TagDef }
  | { mode: "rename"; tag: TagDef };

function TagList({
  kind,
  title,
  hint,
  counts,
  isAdmin,
  onChanged,
}: {
  kind: TagKind;
  title: string;
  hint: string;
  counts: Record<string, number> | null;
  isAdmin: boolean;
  onChanged: () => void;
}) {
  const { data, loading } = useTagDefs(kind);
  const [filter, setFilter] = useState("");
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [mergeInto, setMergeInto] = useState("");
  const [renameTo, setRenameTo] = useState("");

  const sorted = useMemo(
    () =>
      [...data].sort(
        (a, b) =>
          (a.status === "hidden" ? 1 : 0) - (b.status === "hidden" ? 1 : 0) ||
          (counts?.[b.id] ?? 0) - (counts?.[a.id] ?? 0) ||
          (a.name ?? "").localeCompare(b.name ?? "", "he"),
      ),
    [data, counts],
  );

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((t) => (t.name ?? "").toLowerCase().includes(q));
  }, [sorted, filter]);

  const duplicateNames = useMemo(() => {
    const seen = new Map<string, number>();
    for (const t of data) {
      const key = (t.name ?? "").trim();
      seen.set(key, (seen.get(key) ?? 0) + 1);
    }
    return new Set([...seen.entries()].filter(([, n]) => n > 1).map(([k]) => k));
  }, [data]);

  async function guard(fn: () => Promise<string | null>) {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const m = await fn();
      if (m) setMsg(m);
      onChanged();
    } catch (e) {
      setErr(message(e));
    } finally {
      setBusy(false);
    }
  }

  const add = () =>
    guard(async () => {
      const name = draft.trim();
      if (!name) return null;
      await createTagDef(name, kind);
      setDraft("");
      setAdding(false);
      return null;
    });

  const toggle = (tag: TagDef) =>
    guard(async () => {
      await updateTagDef(tag.id, { status: tag.status === "hidden" ? "active" : "hidden" });
      return null;
    });

  const startDelete = (tag: TagDef) =>
    guard(async () => {
      const r = await deleteTagDef(tag.id, true);
      setPending({ mode: "delete", tag, people: r.people });
      return null;
    });

  const confirmDelete = (tag: TagDef) =>
    guard(async () => {
      const r = await deleteTagDef(tag.id);
      setPending(null);
      return `מחקנו את „${r.tagName}” והסרנו אותה מ־${r.people} אנשי קשר.`;
    });

  const confirmMerge = (tag: TagDef) =>
    guard(async () => {
      if (!mergeInto) return null;
      const r = await mergeTagDefs(tag.id, mergeInto);
      setPending(null);
      setMergeInto("");
      return `איחדנו את „${r.from}” עם „${r.into}”. ${r.people} אנשי קשר עברו לתגית „${r.into}”.`;
    });

  const confirmRename = (tag: TagDef) =>
    guard(async () => {
      const name = renameTo.trim();
      if (!name || name === tag.name) {
        setPending(null);
        return null;
      }
      await updateTagDef(tag.id, { name });
      setPending(null);
      setRenameTo("");
      return `השם עודכן ל־„${name}”. התגית נשארת אצל כל אנשי הקשר שלה.`;
    });

  return (
    <div style={{ marginBottom: 22 }}>
      <div className="section-head" style={{ marginTop: 4 }}>
        <div className="section-ico tint-orange">
          <IcPeople size={17} />
        </div>
        <div className="section-title">
          {title}
          {!loading && <span className="section-n">{data.length}</span>}
        </div>
      </div>

      <p className="faint" style={{ padding: "0 6px 10px", marginTop: 0 }}>
        {hint}
      </p>

      {duplicateNames.size > 0 && (
        <div className="notice error" style={{ marginTop: 0, marginBottom: 12 }}>
          יש כאן {duplicateNames.size} שמות שמופיעים פעמיים. אלה תגיות נפרדות — מומלץ לאחד אותן בלחיצה על „איחוד”.
        </div>
      )}

      {data.length > 6 && (
        <div className="searchbar" style={{ marginTop: 0, marginBottom: 12 }}>
          <IcSearch />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="חיפוש תגית לפי שם…"
          />
        </div>
      )}

      <div className="card">
        {loading ? (
          <p className="muted" style={{ padding: 18, textAlign: "center" }}>
            טוען…
          </p>
        ) : sorted.length === 0 ? (
          <div style={{ padding: "18px 14px", textAlign: "center" }}>
            <p className="muted">הרשימה ריקה.</p>
            <button
              className="btn btn-wide"
              style={{ marginTop: 10 }}
              onClick={() =>
                guard(async () => {
                  const r = await seedTaxonomy();
                  return r.created === 0 ? "הרשימות כבר מלאות. לא הוספנו כלום." : null;
                })
              }
              disabled={busy}
            >
              <IcSeed size={16} /> טעינת רשימת ברירת המחדל
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <p className="muted" style={{ padding: 18, textAlign: "center" }}>
            לא נמצאו תגיות שתואמות לחיפוש „{filter}”.
          </p>
        ) : (
          filtered.map((tag, i) => {
            const open = pending?.tag.id === tag.id ? pending : null;
            const n = counts?.[tag.id];

            return (
              <div
                key={tag.id}
                style={{ borderBottom: i < filtered.length - 1 ? "1px solid var(--line)" : undefined }}
              >
                <div className="list-row" style={{ borderBottom: 0, flexWrap: "wrap", gap: 8 }}>
                  <span
                    className="grow"
                    style={{
                      opacity: tag.status === "hidden" ? 0.5 : 1,
                      textDecoration: tag.status === "hidden" ? "line-through" : undefined,
                    }}
                  >
                    <b>{tag.name}</b>
                    <span className="faint" style={{ display: "block", marginTop: 2 }}>
                      {n === undefined
                        ? tag.source === "historical_import"
                          ? "מהייבוא"
                          : ""
                        : n === 0
                          ? "ללא אנשי קשר משויכים"
                          : `${n.toLocaleString("he-IL")} אנשי קשר`}
                      {duplicateNames.has((tag.name ?? "").trim()) && (
                        <>
                          {" · "}
                          <span style={{ color: "var(--red)" }}>
                            שם כפול — מומלץ לאחד
                          </span>
                        </>
                      )}
                    </span>
                  </span>

                  {isAdmin && !open && (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <button
                        className="tag-act"
                        onClick={() => {
                          setRenameTo(tag.name ?? "");
                          setPending({ mode: "rename", tag });
                        }}
                        disabled={busy}
                      >
                        שינוי שם
                      </button>
                      <button
                        className="tag-act"
                        onClick={() => {
                          setMergeInto("");
                          setPending({ mode: "merge", tag });
                        }}
                        disabled={busy}
                      >
                        איחוד
                      </button>
                      <button
                        className="tag-act"
                        onClick={() => toggle(tag)}
                        disabled={busy}
                      >
                        {tag.status === "hidden" ? "הצגה מחדש" : "הסתרה"}
                      </button>
                      <button
                        className="tag-act-danger"
                        onClick={() => startDelete(tag)}
                        disabled={busy}
                      >
                        מחיקה
                      </button>
                    </div>
                  )}
                </div>

                {open?.mode === "delete" && (
                  <div className="card-pad">
                    <div
                      className="notice error"
                      style={{ margin: 0, display: "flex", gap: 8, alignItems: "flex-start" }}
                    >
                      <span style={{ flexShrink: 0, marginTop: 2 }}>
                        <IcAlert size={16} />
                      </span>
                      <span>
                        {open.people === 0 ? (
                          <>לאף אחד אין את „{tag.name}”. המחיקה לא תשנה כלום אצל אנשי הקשר.</>
                        ) : (
                          <>
                            „{tag.name}” תוסר מ־<b>{open.people.toLocaleString("he-IL")} אנשי קשר</b>{" "}
                            והתגית תימחק. אנשי הקשר עצמם לא נמחקים.
                          </>
                        )}
                      </span>
                    </div>
                    <div className="btn-row" style={{ marginTop: 10 }}>
                      <button className="btn btn-danger" onClick={() => confirmDelete(tag)} disabled={busy}>
                        {busy ? "מוחקים…" : "אישור מחיקת התגית"}
                      </button>
                      <button className="btn" onClick={() => setPending(null)} disabled={busy}>
                        ביטול
                      </button>
                    </div>
                  </div>
                )}

                {open?.mode === "merge" && (
                  <div className="card-pad">
                    <p className="muted">
                      כל מי שיש לו את „{tag.name}”
                      {n !== undefined && n > 0 ? ` (${n.toLocaleString("he-IL")} אנשי קשר)` : ""} יקבל
                      את התגית שתבחרו למטה, ואז <b>„{tag.name}” תימחק</b>.
                    </p>
                    <select
                      className="form-input"
                      value={mergeInto}
                      onChange={(e) => setMergeInto(e.target.value)}
                      disabled={busy}
                      style={{ marginTop: 8 }}
                    >
                      <option value="">בחרו תגית יעד לאיחוד…</option>
                      {sorted
                        .filter((t) => t.id !== tag.id)
                        .map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                            {counts?.[t.id] !== undefined ? ` (${counts[t.id]})` : ""}
                          </option>
                        ))}
                    </select>
                    <div className="btn-row" style={{ marginTop: 10 }}>
                      <button
                        className="btn btn-primary"
                        onClick={() => confirmMerge(tag)}
                        disabled={busy || !mergeInto}
                      >
                        {busy ? "מאחדים…" : "איחוד תגיות"}
                      </button>
                      <button className="btn" onClick={() => setPending(null)} disabled={busy}>
                        ביטול
                      </button>
                    </div>
                  </div>
                )}

                {open?.mode === "rename" && (
                  <div className="card-pad">
                    <input
                      className="form-input"
                      value={renameTo}
                      onChange={(e) => setRenameTo(e.target.value)}
                      autoFocus
                      disabled={busy}
                      onKeyDown={(e) => e.key === "Enter" && confirmRename(tag)}
                    />
                    <div className="btn-row" style={{ marginTop: 10 }}>
                      <button
                        className="btn btn-primary"
                        onClick={() => confirmRename(tag)}
                        disabled={busy}
                      >
                        שמירה
                      </button>
                      <button className="btn" onClick={() => setPending(null)} disabled={busy}>
                        ביטול
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {adding ? (
        <div className="card" style={{ padding: 12 }}>
          <div className="searchbar" style={{ marginTop: 0 }}>
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="שם התגית החדשה…"
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && add()}
            />
          </div>
          <div className="btn-row" style={{ marginTop: 10 }}>
            <button className="btn btn-primary" onClick={add} disabled={busy || !draft.trim()}>
              הוספה
            </button>
            <button
              className="btn"
              onClick={() => {
                setAdding(false);
                setDraft("");
                setErr(null);
              }}
              disabled={busy}
            >
              ביטול
            </button>
          </div>
        </div>
      ) : (
        <button className="btn btn-wide" onClick={() => setAdding(true)} disabled={busy}>
          <IcPlus size={16} /> הוספת תגית חדשה
        </button>
      )}

      {msg && <div className="notice info">{msg}</div>}
      {err && <div className="notice error">{err}</div>}
    </div>
  );
}

function message(e: unknown): string {
  if (e && typeof e === "object" && "message" in e) return String((e as { message: unknown }).message);
  return "לא הצלחנו לבצע את הפעולה. נסו שוב.";
}
